import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from "@/components/ui/SossietyDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCreateTraining, useUpdateTraining } from '@/features/calendar/hooks/useTrainings';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { InstructorDisplay } from '@/components/ui/InstructorDisplay';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { Switch } from "@/components/ui/switch";
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { TrainingPartner } from '@/types/trainings';
import { useToast } from '@/shared/hooks/use-toast';
import { DialogTitle } from '@/components/ui/dialog';
import { useI18n } from '@/contexts/I18nContext';
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig';
import { RecurrenceSelector } from '@/features/calendar/components/RecurrenceSelector';
import { RecurrenceConfig, generateRecurringDates, validateRecurrenceConfig, parseEndDate } from '@/utils/recurrenceUtils';
import { formatDateTimeForDisplay } from '@/utils/timezoneUtils';

interface AddTrainingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrainingCreated: () => void;
  selectedDate?: Date;
  onOpenNotesModal?: (training: any) => void;
  // Edit mode props
  mode?: 'create' | 'edit';
  trainingToEdit?: any;
  onTrainingUpdated?: () => void;
}

interface TrainingForm {
  training_type: 'Group Class' | 'Private Class'; // New: toggle field
  category: string; // New: optional category field (was 'type')
  modality: 'Gi' | 'No-Gi'; // Keep as 'No-Gi' to match existing database
  date: string;
  time: string;
  duration: number;
  academy_id: string;
  instructor_id: string | undefined;
  instructor_name: string | undefined;
}

interface Academy {
  id: string;
  name: string;
}

interface Instructor {
  id: string;
  name?: string;
  full_name: string; // Required to match InstructorAutocomplete interface
  username?: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

import { TRAINING_CATEGORIES, getTrainingCategoryTranslationKey } from '@/constants/trainingCategories';
import { DateInput } from '@/components/ui/DateInput';
import { TimeInput } from '@/components/ui/TimeInput';

export const AddTrainingModal = ({ 
  isOpen, 
  onClose, 
  onTrainingCreated, 
  selectedDate, 
  onOpenNotesModal,
  mode = 'create',
  trainingToEdit,
  onTrainingUpdated
}: AddTrainingModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, currentLanguage } = useI18n();
  // Removed agenda config - not needed for basic training creation
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [academies, setAcademies] = useState<Academy[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [createdTraining, setCreatedTraining] = useState<any>(null);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  const createTrainingMutation = useCreateTraining();
  const updateTrainingMutation = useUpdateTraining();

  const [form, setForm] = useState<TrainingForm>({
    training_type: 'Group Class',
    category: '',
    modality: 'Gi',
    date: '',
    time: '',
    duration: 60,
    academy_id: '',
    instructor_id: undefined,
    instructor_name: undefined
  });

  // Separate state for academy display value
  const [academyValue, setAcademyValue] = useState<{ id: string; name: string } | ''>('');
  const [academyLoading, setAcademyLoading] = useState(false);
  const [instructorValue, setInstructorValue] = useState<Instructor | string | null>(null);
  // Refresh key for InstructorAutocomplete to update manual instructors list
  const [instructorAutocompleteRefreshKey, setInstructorAutocompleteRefreshKey] = useState(0);
  
  // Loading state for recurring trainings creation
  const [isCreatingRecurring, setIsCreatingRecurring] = useState(false);
  
  // Recurrence state - Default to "Does not repeat" (none)
  const [recurrenceConfig, setRecurrenceConfig] = useState<RecurrenceConfig>({
    enabled: false,
    frequency: 'none',
  });

  // Load all academies when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadUserAcademies();
    }
  }, [isOpen, user]);

  // Clear form and load user's main academy when modal opens in create mode
  useEffect(() => {
    if (isOpen && mode === 'create') {
      // Reset form for new training
      setForm({
        training_type: 'Group Class',
        category: '',
        modality: 'Gi',
        date: '', // Empty date field for new training
        time: '', // Empty time field for new training
        duration: 60,
        academy_id: '',
        instructor_id: undefined,
        instructor_name: undefined
      });
      setAcademyValue('');
      setInstructorValue(null);
      setStep('form');
      // Reset recurrence config to default (none)
      setRecurrenceConfig({
        enabled: false,
        frequency: 'none',
      });

      // Load user's main academy after form reset
      const loadUserMainAcademy = async () => {
        if (user?.id) {
          try {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('main_academy_id')
              .eq('id', user.id)
              .single();
            
            if (profileData?.main_academy_id) {
              const { data: academyData } = await supabase
                .from('academies')
                .select('id, name')
                .eq('id', profileData.main_academy_id)
                .single();
              
              if (academyData) {
                setAcademyValue({ id: academyData.id, name: academyData.name });
                setForm(prev => ({ ...prev, academy_id: academyData.id }));
              }
            }
          } catch (error) {
            console.error('Error loading user academy:', error);
          }
        }
      };

      loadUserMainAcademy();
    }
  }, [isOpen, mode, user?.id]);

  // Reset step when modal opens in edit mode
  useEffect(() => {
    if (isOpen && mode === 'edit') {
      setStep('form');
      setCreatedTraining(null);
    }
  }, [isOpen, mode]);

  // Debug form changes
  useEffect(() => {
    // console.log('🔄 AddTrainingModal - Form state changed:', {
    //   instructor_id: form.instructor_id,
    //   instructor_name: form.instructor_name,
    //   instructorValue: instructorValue
    // });
  }, [form.instructor_id, form.instructor_name, instructorValue]);

  // Clear instructor state when modal opens
  useEffect(() => {
    if (isOpen && mode === 'edit') {
      // console.log('🔄 AddTrainingModal - Modal opened in edit mode, clearing instructor state');
      setForm(prev => ({ 
        ...prev, 
        instructor_id: undefined,
        instructor_name: undefined
      }));
      setInstructorValue(null);
    }
  }, [isOpen, mode]);


  useEffect(() => {
    if (isOpen && mode === 'create' && selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      setForm(prev => ({ ...prev, date: dateStr }));
    }
  }, [isOpen, selectedDate, mode]);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'create') {
        setCreatedTraining(null);
      } else if (mode === 'edit' && trainingToEdit) {
        const { date: displayDate, time: displayTime } = trainingToEdit.start_time 
          ? formatDateTimeForDisplay(trainingToEdit.start_time)
          : { date: format(new Date(), 'yyyy-MM-dd'), time: '6:00 PM' };
        
        setForm({
          training_type: trainingToEdit.training_type || 'Group Class',
          category: trainingToEdit.category || '',
          modality: trainingToEdit.modality || 'Gi',
          date: displayDate,
          time: displayTime,
          duration: trainingToEdit.duration || 60,
          academy_id: trainingToEdit.academy_id || '',
          instructor_id: trainingToEdit.instructor_id || undefined,
          instructor_name: trainingToEdit.instructor_name || undefined
        });
        
        loadTrainingDetails(trainingToEdit);
      }
    }
  }, [isOpen, mode, trainingToEdit]);

  const loadTrainingDetails = async (training: any) => {
    // console.log('🔄 AddTrainingModal - loadTrainingDetails called with training:', training);
    try {
      // Load academy details
      if (training.academy_id) {
        const { data: academyData, error: academyError } = await supabase
          .from('academies')
          .select('id, name')
          .eq('id', training.academy_id)
          .single();

        if (!academyError && academyData) {
          setAcademies(prev => {
            const exists = prev.find(a => a.id === academyData.id);
            return exists ? prev : [...prev, academyData];
          });
          // Set the academy value for display
          setAcademyValue({ id: academyData.id, name: academyData.name });
        }
      }

      // Always fetch the latest instructor data from the database
      // console.log('🔄 AddTrainingModal - Fetching latest instructor data for training:', training.id);
      
      // Add a small delay to ensure database changes are propagated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      try {
        // Use local Supabase functions URL in development
        const baseUrl = import.meta.env.DEV
          ? 'http://127.0.0.1:54321'
          : import.meta.env.VITE_SUPABASE_URL;

        const response = await fetch(`${baseUrl}/functions/v1/get_training_instructor`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            training_id: training.id,
            _t: Date.now() // Cache busting parameter
          })
        });

        // console.log('🔄 AddTrainingModal - API response status:', response.status);
        if (response.ok) {
          const result = await response.json();
          // console.log('🔄 AddTrainingModal - API result:', result);
          if (result.instructor) {
            // console.log('🔄 AddTrainingModal - Setting instructor value:', result.instructor);
            // Handle manual instructor vs sOSSiety user instructor
            if (result.instructor.is_manual && result.instructor.manual_name) {
              // For manual instructors, use the name as a string
              setInstructorValue(result.instructor.manual_name);
              setForm(prev => ({ 
                ...prev, 
                instructor_id: undefined, // Manual instructors don't have user_id
                instructor_name: result.instructor.manual_name
              }));
            } else {
              // For sOSSiety user instructors, use an object
              const instructorName = result.instructor.name || result.instructor.manual_name || '';
              const instructorValue: Instructor = { 
                id: result.instructor.instructor_user_id || result.instructor.id, 
                name: instructorName,
                full_name: instructorName, // Required field
                username: result.instructor.username || '',
                profile_picture_url: result.instructor.avatar_url,
                belt_rank: result.instructor.belt_rank
              };
              setInstructorValue(instructorValue);
              setForm(prev => ({ 
                ...prev, 
                instructor_id: result.instructor.instructor_user_id || result.instructor.id,
                instructor_name: instructorName
              }));
            }
          } else {
            // console.log('🔄 AddTrainingModal - No instructor found, clearing form');
            // No instructor found, clear the form
            setForm(prev => ({ 
              ...prev, 
              instructor_id: undefined,
              instructor_name: undefined
            }));
            setInstructorValue(null);
          }
        } else {
          // console.log('🔄 AddTrainingModal - API error, clearing form');
          // API error, clear the form
          setForm(prev => ({ 
            ...prev, 
            instructor_id: undefined,
            instructor_name: undefined
          }));
          setInstructorValue(null);
        }
      } catch (error) {
        // console.error('Error loading instructor details:', error);
        // Error occurred, clear the form
        setForm(prev => ({ 
          ...prev, 
          instructor_id: undefined,
          instructor_name: undefined
        }));
        setInstructorValue(null);
      }
    } catch (error) {
      // console.error('Error loading training details:', error);
    }
  };

  const loadUserAcademies = async () => {
    setAcademyLoading(true);
    try {
      const { data: academiesData, error: academiesError } = await supabase
        .from('academies')
        .select('id, name')
        .order('name');

      if (academiesError) throw academiesError;
      setAcademies(academiesData || []);
      
      // Load user's main academy
      if (user) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('main_academy_id')
          .eq('id', user.id)
          .single();

        if (!profileError && profileData?.main_academy_id) {
          const userAcademy = academiesData?.find(academy => academy.id === profileData.main_academy_id);
          if (userAcademy) {
            setAcademyValue(userAcademy);
            setForm(prev => ({ ...prev, academy_id: userAcademy.id }));
          }
        }
      }
    } catch (error) {
      // console.error('Error loading academies:', error);
    } finally {
      setAcademyLoading(false);
    }
  };

  const isFormValid = () => {
    // In edit mode, we need to check if there are any changes to the form
    if (mode === 'edit' && trainingToEdit) {
      // Check if any field has changed from the original training
      const hasChanges = 
        form.training_type !== trainingToEdit.training_type ||
        form.modality !== trainingToEdit.modality ||
        form.category !== trainingToEdit.category ||
        form.duration !== trainingToEdit.duration ||
        form.academy_id !== trainingToEdit.academy_id ||
        form.instructor_id !== trainingToEdit.instructor_id ||
        form.instructor_name !== trainingToEdit.instructor_name ||
        form.date !== (trainingToEdit.start_time ? format(new Date(trainingToEdit.start_time), 'yyyy-MM-dd') : '') ||
        form.time !== trainingToEdit.time;
      
      return hasChanges;
    }
    
    // In create mode, check if required fields are filled
    // If recurrence is enabled, time and endDate are required (date comes from recurrence)
    if (recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none') {
      return form.time !== '' && !!recurrenceConfig.endDate;
    }
    // Otherwise, both date and time are required
    const valid = form.date !== '' && form.time !== '';
    return valid;
  };

  const convertTimeTo24Hour = (timeString: string) => {
    const [time, period] = timeString.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    // 12 PM deve permanecer 12 (meio-dia)
    // 12 AM deve se tornar 0 (meia-noite)
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || !user) return;

    try {

      // In edit mode, if form.time is empty, use the original training time
      let timeToUse = form.time;
      // console.log('🔄 AddTrainingModal - Debug time extraction:', {
      //   mode,
      //   formTime: form.time,
      //   trainingToEdit: trainingToEdit,
      //   trainingToEditStartTime: trainingToEdit?.start_time
      // });
      
      if (mode === 'edit' && (!form.time || form.time === '') && trainingToEdit) {
        // Convert the original training time to AM/PM format
        const originalStartTime = new Date(trainingToEdit.start_time);
        // console.log('🔄 AddTrainingModal - Original start time:', originalStartTime);
        
        const timeString = `${originalStartTime.getHours()}:${originalStartTime.getMinutes().toString().padStart(2, '0')}`;
        // console.log('🔄 AddTrainingModal - Time string:', timeString);
        
        timeToUse = formatTimeToAMPM(timeString) || '';
        // console.log('🔄 AddTrainingModal - Formatted time:', timeToUse);
      }
      
      // console.log('🔄 AddTrainingModal - Final timeToUse:', timeToUse);
      
      if (!timeToUse) {
        // console.error('No time available for training');
        return;
      }

      // In edit mode, if form.date is empty, use the original training date
      // If recurrence is enabled, we don't need a specific date (will use today or first occurrence)
      let dateToUse = form.date;
      
      if (recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none') {
        // For recurrence, use today's date as the base (the actual dates come from generateRecurringDates)
        dateToUse = new Date().toISOString().split('T')[0];
      } else if (mode === 'edit' && (!form.date || form.date === '') && trainingToEdit) {
        const originalStartTime = new Date(trainingToEdit.start_time);
        dateToUse = `${originalStartTime.getFullYear()}-${(originalStartTime.getMonth() + 1).toString().padStart(2, '0')}-${originalStartTime.getDate().toString().padStart(2, '0')}`;
      }
      
      if (!dateToUse) {
        // console.error('No date available for training');
        return;
      }

      const time24Hour = convertTimeTo24Hour(timeToUse);
      const [hours, minutes] = time24Hour.split(':').map(Number);
      
      const [year, month, day] = dateToUse.split('-').map(Number);
      
      // Create date in local timezone to preserve user's chosen time
      // For recurrence, this is just the base date - actual dates come from generateRecurringDates
      const startTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + form.duration);
      
      // Use local time directly - Supabase will handle timezone conversion
      const utcStartTime = startTime;
      const utcEndTime = endTime;

      if (mode === 'edit' && trainingToEdit) {
        // Update existing training
        const updateData = {
          title: form.training_type, // Use training_type as title
          start_time: utcStartTime.toISOString(),
          end_time: utcEndTime.toISOString(),
          modality: form.modality,
          type: form.training_type as 'Group Class' | 'Private Class', // Use the selected training type
          category: form.category, // Use selected category
          duration: form.duration,
          academy_id: form.academy_id || null // Allow null academy_id
        };

        await updateTrainingMutation.mutateAsync({
          trainingId: trainingToEdit.id,
          trainingData: updateData,
          instructorId: form.instructor_id || undefined
        });

        // Update instructor if provided (manual or sOSSiety user)
        const instructorNameToUse =
          form.instructor_name ||
          (typeof instructorValue === 'string'
            ? instructorValue
            : undefined);

        if (form.instructor_id || instructorNameToUse) {
          try {
            const instructorData: any = {
              training_id: trainingToEdit.id,
            };

            if (form.instructor_id) {
              // sOSSiety user instructor
              instructorData.instructor_user_id = form.instructor_id;
            } else if (instructorNameToUse) {
              // Manual instructor
              instructorData.manual_name = instructorNameToUse;
            }

            const { getEdgeFunctionUrl } = await import('@/utils/url');
            const functionUrl = getEdgeFunctionUrl('add_training_instructor');
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            if (token && anonKey) {
              try {
                const response = await fetch(functionUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'apikey': anonKey, // Required for Supabase edge functions
                  },
                  body: JSON.stringify(instructorData),
                });
                
                const responseText = await response.text();
                
                if (!response.ok) {
                  console.error('Error adding instructor to recurring training:', response.status, response.statusText, responseText);
                }
              } catch (fetchError: any) {
                console.error('Error calling edge function to add instructor:', fetchError?.message || fetchError);
              }
            } else {
              if (!token) console.warn('No auth token available to add instructor');
              if (!anonKey) console.warn('No anon key available to add instructor (apikey header missing)');
            }
          } catch (error) {
            console.error('Error updating instructor:', error);
          }
        }

        toast({
          title: "Training updated successfully!",
          description: "Your training has been updated.",
          duration: 2000,
        });

        onTrainingUpdated?.();
        setStep('form');
        setCreatedTraining(null);
        // Only close if onTrainingUpdated is not provided (it handles closing)
        if (!onTrainingUpdated) {
          onClose();
        }
      } else {
        // Create new training(s) - with or without recurrence
        const baseTrainingData = {
          title: form.training_type,
          modality: form.modality,
          status: 'Scheduled' as const,
          type: form.training_type as 'Group Class' | 'Private Class', // Use the selected training type
          category: form.category,
          duration: form.duration,
          academy_id: form.academy_id || null,
          instructor_id: form.instructor_id,
          privacy: 'connections' as const,
        };

        // Check if recurrence is enabled
        if (
          recurrenceConfig.enabled &&
          recurrenceConfig.frequency !== 'none' &&
          recurrenceConfig.endDate
        ) {
          // Validate recurrence config
          const validation = validateRecurrenceConfig(
            recurrenceConfig,
            startTime,
            3
          );
          
          if (!validation.isValid) {
            toast({
              title: 'Validation Error',
              description: validation.error || 'Invalid recurrence configuration',
              variant: 'destructive',
              duration: 3000,
            });
            return;
          }

      // Generate recurring dates
      // Parse endDate from YYYY-MM-DD string and set to end of day in local timezone
      const endDate = parseEndDate(recurrenceConfig.endDate);
      
      const recurringDates = generateRecurringDates({
        startDate: startTime,
        frequency: recurrenceConfig.frequency,
        daysOfWeek: recurrenceConfig.daysOfWeek,
        endDate,
        duration: form.duration,
      });

          if (recurringDates.length === 0) {
            toast({
              title: 'Error',
              description: 'No dates generated for recurrence. Please check your configuration.',
              variant: 'destructive',
              duration: 3000,
            });
            return;
          }

          // Filter out dates that have already passed (for today, only include if time hasn't passed)
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const filteredDates = recurringDates.filter(date => {
            const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            // If it's today, check if the time has passed
            if (dateOnly.getTime() === today.getTime()) {
              return date > now; // Only include if the time hasn't passed
            }
            // For future dates, always include
            return date > now;
          });

          if (filteredDates.length === 0) {
            toast({
              title: 'Error',
              description: 'No valid dates found. All selected dates and times have already passed.',
              variant: 'destructive',
              duration: 3000,
            });
            return;
          }

          // Create multiple trainings
          setIsCreatingRecurring(true);
          let successCount = 0;
          let errorCount = 0;
          let instructorErrorCount = 0;
          const createdTrainings: any[] = [];

          try {
            for (let i = 0; i < filteredDates.length; i++) {
              const date = filteredDates[i];
              
              const trainingData = {
                ...baseTrainingData,
                start_time: date.toISOString(),
                end_time: new Date(
                  date.getTime() + form.duration * 60000
                ).toISOString(),
              };

              try {
                const data = await createTrainingMutation.mutateAsync(
                  trainingData
                );

                createdTrainings.push(data);
                successCount++;

                // Add instructor to each training if provided
                const instructorNameToUse =
                  form.instructor_name ||
                  (typeof instructorValue === 'string'
                    ? instructorValue
                    : undefined);

                if (form.instructor_id || instructorNameToUse) {
                  try {
                    const instructorData: any = {
                      training_id: data.id,
                    };

                    if (form.instructor_id) {
                      instructorData.instructor_user_id = form.instructor_id;
                    } else if (instructorNameToUse) {
                      instructorData.manual_name = instructorNameToUse;
                    }

                    const { getEdgeFunctionUrl } = await import('@/utils/url');
                    const functionUrl = getEdgeFunctionUrl(
                      'add_training_instructor'
                    );
                    const { data: { session } } = await supabase.auth.getSession();
                    const token = session?.access_token;
                    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

                    if (token && anonKey) {
                      try {
                        const response = await fetch(functionUrl, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                            'apikey': anonKey, // Required for Supabase edge functions
                          },
                          body: JSON.stringify(instructorData),
                        });

                        const responseText = await response.text();

                        if (!response.ok) {
                          console.error(`Error adding instructor to training ${data.id}:`, response.status, responseText);
                          instructorErrorCount++;
                        }
                      } catch (fetchError: any) {
                        console.error(`Error calling edge function for training ${data.id}:`, fetchError?.message || fetchError);
                        instructorErrorCount++;
                      }
                    } else {
                      if (!token) {
                        console.error('No auth token available to add instructor');
                        instructorErrorCount++;
                      }
                      if (!anonKey) {
                        console.error('No anon key available to add instructor (apikey header missing)');
                        instructorErrorCount++;
                      }
                    }
                  } catch (error) {
                    console.error(`Error adding instructor to training ${data.id}:`, error);
                    instructorErrorCount++;
                  }
                }
              } catch (error) {
                console.error(`Error creating training for date ${date.toISOString()}:`, error);
                errorCount++;
                // Error creating training - continue with next
              }
            }
          } finally {
            setIsCreatingRecurring(false);
          }

          // Show feedback
          if (successCount > 0) {
            toast({
              title: t('calendar.created_recurring_trainings', {
                count: successCount,
              }),
              description: `Successfully created ${successCount} training${successCount > 1 ? 's' : ''}${instructorErrorCount > 0 ? ` (${instructorErrorCount} instructor${instructorErrorCount > 1 ? 's' : ''} failed to add)` : ''}`,
              duration: 5000,
            });
          }
          if (errorCount > 0) {
            toast({
              title: t('calendar.failed_to_create_trainings', {
                count: errorCount,
              }),
              description: `Failed to create ${errorCount} training${errorCount > 1 ? 's' : ''}`,
              variant: 'destructive',
              duration: 3000,
            });
          }
          if (instructorErrorCount > 0 && successCount > 0) {
            // Show separate warning if instructors failed but trainings were created
            toast({
              title: 'Warning',
              description: `Some instructors could not be added to ${instructorErrorCount} training${instructorErrorCount > 1 ? 's' : ''}. You may need to add them manually.`,
              variant: 'destructive',
              duration: 5000,
            });
          }

          // Use first created training for success screen
          if (createdTrainings.length > 0) {
            setCreatedTraining(createdTrainings[0]);
            setStep('success');
            onTrainingCreated();
          }
        } else {
          // Create single training (no recurrence)
          const data = await createTrainingMutation.mutateAsync({
            ...baseTrainingData,
            start_time: utcStartTime.toISOString(),
            end_time: utcEndTime.toISOString(),
          });

      // Add instructor to training if provided (manual or sOSSiety user)
      const instructorNameToUse =
        form.instructor_name ||
        (typeof instructorValue === 'string'
          ? instructorValue
          : undefined);

      if (form.instructor_id || instructorNameToUse) {
        try {
          const instructorData: any = {
            training_id: data.id,
          };

          if (form.instructor_id) {
            // sOSSiety user instructor
            instructorData.instructor_user_id = form.instructor_id;
          } else if (instructorNameToUse) {
            // Manual instructor
            instructorData.manual_name = instructorNameToUse;
          }

          const { getEdgeFunctionUrl } = await import('@/utils/url');
          const functionUrl = getEdgeFunctionUrl('add_training_instructor');
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          if (token && anonKey) {
            try {
              const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                  'apikey': anonKey, // Required for Supabase edge functions
                },
                body: JSON.stringify(instructorData),
              });
              
              // Wait for response to complete
              const responseText = await response.text();
              
              if (!response.ok) {
                console.error('Error adding instructor:', response.status, response.statusText, responseText);
                // Don't throw error - training was created successfully, instructor is optional
                // But log it for debugging
              } else {
                // Verify the instructor was actually added
                try {
                  const result = JSON.parse(responseText);
                  if (result && !result.success) {
                    console.error('Edge function returned error:', result.error || result.message);
                  }
                } catch (parseError) {
                  console.error('Failed to parse edge function response:', responseText);
                }
              }
            } catch (fetchError: any) {
              console.error('Error calling edge function to add instructor:', fetchError?.message || fetchError);
            }
          } else {
            if (!token) {
              console.warn('No auth token available to add instructor');
            }
            if (!anonKey) {
              console.warn('No anon key available to add instructor (apikey header missing)');
            }
          }
        } catch (error) {
          console.error('Error adding instructor to training:', error);
        }
      }

          setCreatedTraining(data);
          setStep('success');
          onTrainingCreated();
        }
      }
    } catch (error) {
      console.error('Error saving training:', error);
      setIsCreatingRecurring(false); // Ensure loading state is reset on error
      toast({
        title: 'Error',
        description: 'Failed to create training. Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
    }
  };

  const handleClose = () => {
    setStep('form');
    setForm({
      training_type: 'Group Class', // Default to Group Class
      category: '', // Default to empty
      modality: 'Gi',
      date: '',
      time: '',
      duration: 60,
      academy_id: '',
      instructor_id: undefined,
      instructor_name: undefined
    });
    setAcademyValue('');
    setInstructorValue(null);
    setStep('form');
    setCreatedTraining(null);
    // Reset recurrence config to default (none)
    setRecurrenceConfig({
      enabled: false,
      frequency: 'none',
    });
    onClose();
  };

  const handleSuccessButtonClick = () => {
    if (isCreatedTrainingPast() && onOpenNotesModal && createdTraining) {
      onOpenNotesModal(createdTraining);
    }
    handleClose();
  };

  const handleAcademyChange = (academy: { id: string; name: string } | string) => {
    if (typeof academy === 'object') {
      setForm(prev => ({ 
        ...prev, 
        academy_id: academy.id
      }));
      setAcademyValue(academy);
    } else {
      setForm(prev => ({ 
        ...prev, 
        academy_id: academy
      }));
      // When academy is a string (ID), set to empty string to clear the display
      setAcademyValue('');
    }
  };

  const handleInstructorChange = (instructor: Instructor | string | null) => {
    // console.log('🔄 AddTrainingModal - handleInstructorChange called with:', instructor);
    
    if (instructor === null) {
      // console.log('🔄 AddTrainingModal - Clearing instructor');
      setForm(prev => ({ 
        ...prev, 
        instructor_id: undefined,
        instructor_name: undefined
      }));
      setInstructorValue(null);
      return;
    }

    if (typeof instructor === 'string') {
      // console.log('🔄 AddTrainingModal - Setting manual instructor:', instructor);
      setForm(prev => ({ 
        ...prev, 
        instructor_id: undefined,
        instructor_name: instructor
      }));
      setInstructorValue(instructor);
    } else {
      // console.log('🔄 AddTrainingModal - Setting instructor from object:', instructor);
      setForm(prev => ({ 
        ...prev, 
        instructor_id: instructor.id,
        instructor_name: instructor.full_name || instructor.name || ''
      }));
      setInstructorValue(instructor);
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setForm(prev => ({ ...prev, duration: 0 }));
    } else {
      const numValue = parseInt(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 480) {
        setForm(prev => ({ ...prev, duration: numValue }));
      }
    }
  };

  const isPastTraining = () => {
    if (!form.date || !form.time) return false;
    const time24Hour = convertTimeTo24Hour(form.time);
    const [hours, minutes] = time24Hour.split(':').map(Number);
    
    const [year, month, day] = form.date.split('-').map(Number);
    const trainingTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
    
    return trainingTime < new Date();
  };

  const isCreatedTrainingPast = () => {
    if (!createdTraining) return false;
    const trainingTime = new Date(createdTraining.start_time);
    return trainingTime < new Date();
  };



  if (step === 'success' && createdTraining) {
    const trainingDate = new Date(createdTraining.start_time);
    const isPast = isCreatedTrainingPast();

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl p-4">
          <div className="flex flex-col items-center justify-center text-center px-1 py-6">
            {/* Title */}
            <h3 className="text-xl font-semibold text-black mb-6 font-roboto">
              {t('common.success')}!
            </h3>
            
            {/* Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            {/* Content */}
            <div className="space-y-4 mb-8">
              <p className="text-gray-600">
                {t('calendar.training_added_successfully', { date: format(trainingDate, 'MMMM d, yyyy') })}
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="font-semibold text-black">
                  {createdTraining.title}
                </div>
                <div className="text-gray-600">
                  {format(trainingDate, 'MMMM d, yyyy')} at {format(trainingDate, 'h:mm a')}
                </div>
                <div className="text-gray-500 text-xs font-medium">
                  {createdTraining.modality}
                </div>
              </div>

              {isPast && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-700 text-sm">
                    {t('calendar.training_past_redirect_notes')}
                  </p>
                </div>
              )}
            </div>

            {/* Button */}
            <Button
              variant="primary"
              size="lg"
              onClick={handleSuccessButtonClick}
              className="w-full"
            >
              {isPast ? t('calendar.add_notes_to_training') : t('common.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogTitle className="sr-only">{mode === 'edit' ? 'Edit Training' : 'Add Training'}</DialogTitle>
        <SossietyDialogHeader title={mode === 'edit' ? t('calendar.edit_training') : t('calendar.add_training')} titleClassName="text-lg">
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          <div className="space-y-4">
          {/* Type of Training - Toggle */}
          <div>
            <Label className="text-black">{t('calendar.training_type')}*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.training_type === 'Group Class'
                    ? 'bg-sossiety-red text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, training_type: 'Group Class' }))}
              >
                {t('calendar.group_class')}
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.training_type === 'Private Class'
                    ? 'bg-sossiety-red text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, training_type: 'Private Class' }))}
              >
                {t('calendar.private_class')}
              </button>
            </div>
          </div>

          {/* Modality - Gi/No-Gi Toggle */}
          <div>
            <Label className="text-black">{t('calendar.modality')}*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.modality === 'Gi'
                    ? 'bg-sossiety-red text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, modality: 'Gi' }))}
              >
                Gi
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  form.modality === 'No-Gi'
                    ? 'bg-sossiety-red text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setForm(prev => ({ ...prev, modality: 'No-Gi' }))}
              >
                No-Gi
              </button>
            </div>
          </div>

          {/* Category (Optional) */}
          <div>
            <Label className="text-black">{t('calendar.category')} ({t('common.optional')})</Label>
            <div className="w-full border border-gray-300 rounded-md focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-400">
              <Select
                value={form.category}
                onValueChange={(value) => setForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent text-sm [&_svg]:text-gray-500">
                  <SelectValue placeholder={t('calendar.select_category')} />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  {TRAINING_CATEGORIES.map((category) => {
                    const categoryKey = getTrainingCategoryTranslationKey(category);
                    return (
                      <SelectItem key={category} value={category} className="text-white text-sm truncate">
                        {t(`calendar.training_categories.${categoryKey}`) || category}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date and Time */}
          {/* Hide Date when recurrence is enabled, show Time with full width */}
          {recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none' ? (
            <div className="w-full">
              <Label htmlFor="training-time" className="text-black text-sm block mb-1">{t('calendar.time_field')}*</Label>
              <TimeInput
                id="training-time"
                value={form.time}
                onChange={(v) => setForm(prev => ({ ...prev, time: v }))}
                listId="training-time-options"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 items-start">
              <div className="w-full">
                <Label htmlFor="training-date" className="text-black text-sm block mb-1">{t('calendar.date')}*</Label>
                <DateInput
                  id="training-date"
                  value={form.date}
                  onChange={(v) => setForm(prev => ({ ...prev, date: v }))}
                />
              </div>
              <div className="w-full">
                <Label htmlFor="training-time" className="text-black text-sm block mb-1">{t('calendar.time_field')}*</Label>
                <TimeInput
                  id="training-time"
                  value={form.time}
                  onChange={(v) => setForm(prev => ({ ...prev, time: v }))}
                  listId="training-time-options"
                />
              </div>
            </div>
          )}

          {/* Recurrence Selector */}
          {mode === 'create' && (
            <RecurrenceSelector
              value={recurrenceConfig}
              onChange={setRecurrenceConfig}
              maxMonths={3}
              startDate={(() => {
                try {
                  // Use the EXACT SAME logic as in handleSubmit for consistency
                  // This ensures calculateOccurrences uses the same startDate that will be used in creation
                  let dateToUse = form.date;
                  if (recurrenceConfig.enabled && recurrenceConfig.frequency !== 'none') {
                    // For recurrence, use today's date as the base (the actual dates come from generateRecurringDates)
                    // This matches handleSubmit line 568
                    dateToUse = new Date().toISOString().split('T')[0];
                  }
                  
                  if (!dateToUse) {
                    // Fallback to today if no date is available
                    const today = new Date();
                    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
                  }
                  
                  const [year, month, day] = dateToUse.split('-').map(Number);
                  
                  // Use form.time if available, otherwise use midnight
                  // This matches handleSubmit lines 579-586
                  if (form.time) {
                    const time24Hour = convertTimeTo24Hour(form.time);
                    const [hours, minutes] = time24Hour.split(':').map(Number);
                    // Create date in local timezone with the time from form
                    // This matches handleSubmit line 586
                    return new Date(year, month - 1, day, hours, minutes, 0, 0);
                  }
                  
                  // Return date at midnight if no time is available
                  return new Date(year, month - 1, day, 0, 0, 0, 0);
                } catch {
                  // Fallback to today if any error occurs
                  const today = new Date();
                  return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
                }
              })()}
            />
          )}

          {/* Duration */}
          <div>
            <Label htmlFor="duration" className="text-black">
              {t('calendar.duration')} ({t('calendar.time.minutes')})*
            </Label>
            <Input
              id="duration"
              type="number"
              value={form.duration || ''}
              onChange={handleDurationChange}
              className="text-black border-gray-300 bg-white"
              min="15"
              max="480"
              step="15"
              placeholder="60"
            />
          </div>

          {/* Location/Academy */}
          <div>
            <Label className="text-black">{t('calendar.academy')}</Label>
            {academyLoading ? (
              <div className="w-full h-10 bg-gray-200 animate-pulse rounded-md" />
            ) : (
              <AcademyAutocomplete
                value={academyValue}
                onChange={handleAcademyChange}
                placeholder={t('calendar.search_academy_placeholder')}
                containerClassName="w-full"
                theme="light"
              />
            )}
          </div>

          {/* Instructor */}
          <div>
            <Label className="text-black">{t('calendar.instructor')}</Label>
            <InstructorAutocomplete
              value={instructorValue || null}
              onChange={handleInstructorChange}
              placeholder={t('calendar.search_instructor_placeholder')}
              containerClassName="w-full"
              theme="light"
              noPortal
            />
            
            {/* Selected Instructor Display */}
            {instructorValue && (
              <div className="mt-3">
                {typeof instructorValue === 'string' ? (
                  // Manual instructor (string)
                  <InstructorDisplay
                    instructor={{
                      id: 'manual',
                      name: instructorValue,
                      full_name: instructorValue,
                      belt_rank: 'black' // Manual instructors default to black belt
                    }}
                    showRemoveButton={true}
                    onRemove={() => handleInstructorChange(null)}
                    size="md"
                    className="p-2 rounded-lg"
                  />
                ) : (
                  // sOSSiety user instructor (object)
                  <InstructorDisplay
                    instructor={{
                      id: instructorValue.id,
                      name: instructorValue.full_name || instructorValue.name || '',
                      full_name: instructorValue.full_name || instructorValue.name || '',
                      username: instructorValue.username,
                      profile_picture_url: instructorValue.profile_picture_url,
                      belt_rank: instructorValue.belt_rank
                    }}
                    showRemoveButton={true}
                    onRemove={() => handleInstructorChange(null)}
                    size="md"
                    className="p-2 rounded-lg"
                  />
                )}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Fixed Action Button at bottom */}
        <div className="flex-shrink-0 pt-4 pb-6 px-6 border-t border-gray-200 bg-white">
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!isFormValid() || createTrainingMutation.isPending || updateTrainingMutation.isPending || isCreatingRecurring}
            className="w-full"
          >
            {mode === 'edit' 
              ? (updateTrainingMutation.isPending ? t('common.loading') : t('common.update'))
              : (createTrainingMutation.isPending || isCreatingRecurring ? t('common.loading') : t('common.create'))
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 